import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BookingConfirmation = ({ 
  bookingData, 
  onNewBooking, 
  onGoHome 
}) => {
  const { service, date, time, client, bookingId } = bookingData;

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={40} color="var(--color-success)" />
        </div>
        <h1 className="text-3xl font-heading font-bold text-primary mb-2">
          Booking Confirmed!
        </h1>
        <p className="text-muted-foreground font-body">
          Your photography session has been successfully scheduled
        </p>
      </div>
      {/* Booking Details Card */}
      <div className="bg-card rounded-lg border border-border p-6 mb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Icon name="Calendar" size={20} color="var(--color-accent)" />
          <h2 className="text-lg font-heading font-semibold text-primary">
            Session Details
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            <div>
              <p className="text-sm font-body font-medium text-muted-foreground mb-1">
                Booking ID
              </p>
              <p className="font-mono text-accent font-medium">
                #{bookingId}
              </p>
            </div>

            <div>
              <p className="text-sm font-body font-medium text-muted-foreground mb-1">
                Service
              </p>
              <p className="font-body text-foreground font-medium">
                {service?.name}
              </p>
              <p className="text-sm text-muted-foreground">
                Duration: {service?.duration}
              </p>
            </div>

            <div>
              <p className="text-sm font-body font-medium text-muted-foreground mb-1">
                Date & Time
              </p>
              <p className="font-body text-foreground font-medium">
                {formatDate(date)}
              </p>
              <p className="text-sm text-muted-foreground">
                {time?.time}
              </p>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <div>
              <p className="text-sm font-body font-medium text-muted-foreground mb-1">
                Client
              </p>
              <p className="font-body text-foreground font-medium">
                {client?.firstName} {client?.lastName}
              </p>
              <p className="text-sm text-muted-foreground">
                {client?.email}
              </p>
              <p className="text-sm text-muted-foreground">
                {client?.phone}
              </p>
            </div>

            <div>
              <p className="text-sm font-body font-medium text-muted-foreground mb-1">
                Location
              </p>
              <p className="font-body text-foreground">
                {client?.location}
              </p>
            </div>

            <div>
              <p className="text-sm font-body font-medium text-muted-foreground mb-1">
                Total Cost
              </p>
              <p className="text-2xl font-heading font-bold text-accent">
                ₹{service?.price}
              </p>
            </div>
          </div>
        </div>

        {/* Special Requests */}
        {client?.specialRequests && (
          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-sm font-body font-medium text-muted-foreground mb-2">
              Special Requests
            </p>
            <p className="font-body text-foreground bg-muted p-3 rounded-lg">
              {client?.specialRequests}
            </p>
          </div>
        )}
      </div>
      {/* Next Steps */}
      <div className="bg-accent/10 rounded-lg border border-accent/20 p-6 mb-6">
        <h3 className="font-heading font-semibold text-accent mb-4">
          What happens next?
        </h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-accent-foreground">1</span>
            </div>
            <div>
              <p className="font-body font-medium text-foreground">
                Confirmation Email
              </p>
              <p className="text-sm text-muted-foreground">
                You'll receive a detailed confirmation email within 5 minutes
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-accent-foreground">2</span>
            </div>
            <div>
              <p className="font-body font-medium text-foreground">
                Pre-Session Call
              </p>
              <p className="text-sm text-muted-foreground">
                We'll call you 24-48 hours before to discuss details and preparation
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-accent rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-accent-foreground">3</span>
            </div>
            <div>
              <p className="font-body font-medium text-foreground">
                Session Day
              </p>
              <p className="text-sm text-muted-foreground">
                Arrive 15 minutes early. We'll handle the rest!
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Contact Information */}
      <div className="bg-card rounded-lg border border-border p-6 mb-8">
        <h3 className="font-heading font-semibold text-primary mb-4">
          Need to make changes?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <Icon name="Phone" size={18} color="var(--color-accent)" />
            <div>
              <p className="font-body font-medium text-foreground">
                Call us
              </p>
              <p className="text-sm text-muted-foreground">
                (555) 123-4567
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Icon name="Mail" size={18} color="var(--color-accent)" />
            <div>
              <p className="font-body font-medium text-foreground">
                Email us
              </p>
              <p className="text-sm text-muted-foreground">
                hello@studiomusu.com
              </p>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          Please contact us at least 24 hours in advance for any changes or cancellations.
        </p>
      </div>
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          variant="default"
          size="lg"
          onClick={onGoHome}
          iconName="Home"
          iconPosition="left"
          fullWidth
        >
          Back to Home
        </Button>
        <Button
          variant="outline"
          size="lg"
          onClick={onNewBooking}
          iconName="Plus"
          iconPosition="left"
          fullWidth
        >
          Book Another Session
        </Button>
      </div>
    </div>
  );
};

export default BookingConfirmation;